import { roundMoney } from "./calculateTotal";

/**
 * Gross service line total from scope-of-work costOverride (aligned with BasicJob / quote rows).
 */
export function getGrossLineFromCostOverride(co: Record<string, unknown> | null | undefined): number | null {
  if (!co || typeof co !== "object") return null;
  const by = String(co.by ?? "");
  const value = Number(co.value ?? co.price ?? co.amount ?? 0);
  const units = co.units != null && co.units !== "" ? Number(co.units) : 1;

  if (!Number.isFinite(value) || value < 0) return null;
  if (!Number.isFinite(units) || units <= 0) return null;

  if (by === "per_item" || by === "per_square_foot" || by === "per_unit" || by === "per_sf") {
    return roundMoney(units * value);
  }
  if (by === "unique" || by === "monthly" || by === "yearly") {
    return roundMoney(value);
  }
  if (value > 0) {
    return roundMoney(units * value);
  }
  return null;
}

/**
 * Default line total for final invoice when a deposit was paid: remaining balance (override allowed in UI).
 */
export function computeInvoiceLineDefaultAfterDeposit(
  gross: number,
  depositAvailable: number | null | undefined,
  depositInvoiceUid: string | null | undefined,
): number {
  if (gross <= 0) return 0;
  if (!depositInvoiceUid) return roundMoney(gross);
  const av = Math.max(0, Number(depositAvailable ?? 0));
  if (av <= 0) return roundMoney(gross);
  return roundMoney(Math.max(0, gross - Math.min(av, gross)));
}

/**
 * Line total after deposit: prefers an explicit credited amount (from parent payload) when set,
 * otherwise uses pool + deposit invoice uid (same as computeInvoiceLineDefaultAfterDeposit).
 */
export function computeInvoiceLineAfterDepositCredit(
  gross: number,
  depositInvoiceUid: string | null | undefined,
  depositPoolAvailable: number | null | undefined,
  creditedAmountApplied: number | null | undefined,
): number {
  if (gross <= 0) return 0;
  if (!depositInvoiceUid) return roundMoney(gross);
  const credit = Number(creditedAmountApplied ?? 0);
  if (credit > 0 && Number.isFinite(credit)) {
    return roundMoney(Math.max(0, gross - Math.min(credit, gross)));
  }
  return computeInvoiceLineDefaultAfterDeposit(gross, depositPoolAvailable, depositInvoiceUid);
}
